import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Checkbox, Chip, IconButton, TableCell, TableRow } from '@mui/material';
import { FC, memo } from 'react';
import { BodyRowPresenter } from 'src/pages/TruckShifts/presenters/BodyRow.presenter';
import { CompanyGroup, ShiftCell } from 'src/pages/TruckShifts/truckShiftGrid';

type Props = {
  group: CompanyGroup;
  dateColumns: string[];
  expanded: boolean;
  onToggle: () => void;
  // selection オブジェクトではなく primitive/Set + 安定コールバックで渡す（Component.tsx の
  // useMemo(selection) は selectedTruckIds/selectedDates が変わるたびに新参照になり、
  // 配下の BodyRow/BodyCell まで全再レンダーを引き起こすため）。
  showCheckbox?: boolean;
  selectedTruckIds?: Set<number>;
  selectedDates?: Set<string>;
  onToggleTruck?: (truckId: number) => void;
  onSetTrucksSelected?: (truckIds: number[], selected: boolean) => void;
  onCellClick?: (truckId: number, date: string, cells: ShiftCell[], anchorEl: HTMLElement) => void;
};

export const CompanyGroupPresenter: FC<Props> = memo(({
  group, dateColumns, expanded, onToggle, showCheckbox = false, selectedTruckIds, selectedDates,
  onToggleTruck, onSetTrucksSelected, onCellClick,
}) => {
  // 削除済み（active=false）トラックは一括選択の対象外（全選択・allSelected 判定からも除外）。
  const ids = group.trucks.filter((t) => t.active !== false).map((t) => t.id);
  const selectedCount = selectedTruckIds ? ids.filter((id) => selectedTruckIds.has(id)).length : 0;
  const allSelected = selectedCount > 0 && selectedCount === ids.length;
  const someSelected = selectedCount > 0 && selectedCount < ids.length;

  return (
    <>
      <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
        {/* 会社名帯は 1 列目（車両列位置）を sticky 固定し、日付列(残り)は別セルに分ける（colSpan 1 + N）。
            これで横スクロールしても会社名が左に留まる（Lovable 準拠）。 */}
        <TableCell
          sx={{ width: 280, minWidth: 280, position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#f0f4f8', fontWeight: 700 }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={onToggle}
              aria-label={expanded ? 'グループを折りたたむ' : 'グループを展開する'}
            >
              {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
            {showCheckbox && (
              <Checkbox
                size="small"
                sx={{ p: 0 }}
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => onSetTrucksSelected?.(ids, e.target.checked)}
              />
            )}
            {/* 会社名クリックは折りたたみではなく配下 truck の選択トグル（チェックボックスと同じ）。 */}
            <Box
              sx={{ cursor: showCheckbox ? 'pointer' : 'default' }}
              onClick={showCheckbox ? () => onSetTrucksSelected?.(ids, !allSelected) : undefined}
            >
              {group.companyName}
            </Box>
            <Chip label={`${group.trucks.length}台`} size="small" />
          </Box>
        </TableCell>
        <TableCell colSpan={dateColumns.length} sx={{ backgroundColor: '#f0f4f8' }} />
      </TableRow>
      {expanded && group.trucks.map((truck) => (
        <BodyRowPresenter
          key={truck.id}
          truck={truck}
          dateColumns={dateColumns}
          showCheckbox={showCheckbox}
          selected={selectedTruckIds?.has(truck.id) ?? false}
          selectedDates={selectedDates}
          onToggleTruck={onToggleTruck}
          onCellClick={onCellClick}
        />
      ))}
    </>
  );
});
